import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { SharedService } from '@app/core';
import { environment } from '@env/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Standalone page component for Hyperpure cart iframe with user authentication.
 * Requires both user authentication token AND Hyperpure credentials.
 * 
 * This component validates the user's authentication token before loading the iframe.
 * It initializes the iframe and loads the cart view using two message topics:
 * 1. hyperpure_login - Send login credentials
 * 2. hyperpure_frame_load_cart - Load cart view
 *
 * URL Format with User Token:
 * /purchase-manager/hyperpure-cart?userToken=xxx&token=xxx&store_uuid=xxx&email=xxx&phone=xxx
 *
 * Or use session storage with key 'hyperpureLoginData' (user must still be authenticated)
 */
@Component({
    selector: 'app-hyperpure-cart-iframe',
    templateUrl: './hyperpure-cart-iframe.component.html',
    styleUrls: ['./hyperpure-cart-iframe.component.scss']
})
export class HyperpureCartIframeComponent implements OnInit, OnDestroy {

    @ViewChild('iframeElement') iframeElement!: ElementRef;

    // Iframe state
    iframeUrl: string = 'https://www.hyperpure.com/in/Init';
    iframeUrlSafe: SafeResourceUrl | null = null;
    showIframe = false;
    isLoading = true;

    // Login data
    hyperpureLoginData: any = null;

    // User authentication
    private userToken: string | null = null;
    private isUserAuthenticated = false;
    private userInfo: any = null;

    // Security
    private trustedOrigin = environment.hyperpureTrustedOrigin;
    private listenerCalled = false;

    // Public reference for template
    public environment = environment;

    // Lifecycle
    private destroy$ = new Subject<void>();

    constructor(
        private sanitizer: DomSanitizer,
        private route: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private sharedService: SharedService,
        private priceDiscoveryService: PriceDiscoveryService
    ) { }

    ngOnInit(): void {
        this.setupMessageListener();
        this.authenticateAndLoad();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        document.body.style.overflow = '';
    }

    /**
     * Step 1: Authenticate user, then load Hyperpure data
     */
    private authenticateAndLoad(): void {
        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
            console.log('🔍 Query params received');

            // Extract user token - properly decode URL-encoded token
            let rawToken = params['userToken'] || params['user_token'];

            if (!rawToken) {
                this.showError('User authentication token is missing. Access denied.');
                return;
            }

            // URL decode the token to handle special characters (+, /, =)
            // Angular's ActivatedRoute might not fully decode some characters
            try {
                this.userToken = decodeURIComponent(rawToken);
                console.log('🔐 Token decoded, length:', this.userToken.length);
            } catch (e) {
                console.error('Failed to decode token:', e);
                this.userToken = rawToken;
            }

            // Validate user token
            console.log('🔐 Validating user token...');
            this.validateUserToken(this.userToken).subscribe(
                (isValid) => {
                    if (isValid) {
                        console.log('✅ User authenticated successfully');
                        this.isUserAuthenticated = true;
                        
                        // Now load Hyperpure login data
                        this.loadLoginData();
                    } else {
                        this.showError('Invalid or expired user token. Please login again.');
                    }
                },
                (error) => {
                    console.error('❌ User authentication failed:', error);
                    this.showError('Authentication failed. Please try again.');
                }
            );
        });
    }

    /**
     * Validate user token against backend
     */
    private validateUserToken(token: string): Observable<boolean> {
        const apiUrl = `${environment.authentication.url}${environment.authentication.path}get-user-profile`;
        console.log('📡 Calling API:', apiUrl);
        console.log('🔑 Token (first 50 chars):', token.substring(0, 50) + '...');
        console.log('🔑 Token length:', token.length);
        
        // Call your authentication service to verify the token
        return this.http.get<any>(apiUrl, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).pipe(
            map((response: any) => {
                console.log('📥 API Response:', response);
                // Check if response is successful (MongoDB uses _id field)
                if (response && (response._id || response.id)) {
                    console.log('✅ User validated:', response.name || `${response.firstName} ${response.lastName}`);
                    console.log('✅ Email:', response.email);
                    console.log('✅ Company:', response.company?.name);
                    // Store user info for later use
                    this.userInfo = response;
                    return true;
                }
                console.error('❌ Invalid response structure:', response);
                return false;
            }),
            catchError((error) => {
                console.error('❌ Token validation failed - Full error:', error);
                console.error('Error status:', error.status);
                console.error('Error statusText:', error.statusText);
                console.error('Error error object:', error.error);
                
                // Try to parse the error message
                let errorMessage = 'Authentication failed';
                if (error.error) {
                    if (typeof error.error === 'string') {
                        errorMessage = error.error;
                    } else if (error.error.message) {
                        errorMessage = error.error.message;
                    } else if (error.error.error) {
                        errorMessage = error.error.error;
                    }
                }
                
                console.error('Parsed error message:', errorMessage);
                return of(false);
            })
        );
    }

    /**
     * Step 2: Load login data from query parameters or session storage
     * (Only called after user is authenticated)
     */
    private loadLoginData(): void {
        // Method 1: From query parameters
        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
            console.log('🔍 Loading Hyperpure credentials');
            
            if (params['token'] && params['store_uuid']) {
                // Keep token exactly as received - Hyperpure expects "Bearer " prefix
                let token = params['token'];
                
                // Note: Hyperpure token should include "Bearer " prefix
                // If it doesn't have it, add it automatically
                if (!token.startsWith('Bearer ') && !token.startsWith('bearer ')) {
                    token = 'Bearer ' + token;
                    console.log('🔧 Added "Bearer " prefix to token');
                }
                
                // Build all_store_details array
                const storeDetailsArray = [{
                    store_id: params['store_id'] || '',
                    restaurant_name: params['storeName'] || params['store_name'] || '',
                    restaurant_gstno: params['storeGst'] || params['store_gst'] || '',
                    store_address: params['storeAddress'] || params['store_address'] || '',
                    store_pincode: params['storePincode'] || params['store_pincode'] || ''
                }];
                const all_store_details = JSON.stringify(storeDetailsArray);

                // Build all_store_obj with proper structure
                const allStoreObj = {
                    store_id: params['store_id'] || '',
                    email: params['email'] || '',
                    phone: params['phone'] || '',
                    all_store_details: all_store_details
                };
                
                this.hyperpureLoginData = {
                    token: token,
                    store_uuid: params['store_uuid'],
                    store_id: params['store_id'] || '',
                    email: params['email'] || '',
                    phone: params['phone'] || '',
                    session_id: params['session_id'] || '',
                    ActionData: {},
                    checksum: '',
                    all_store_obj: allStoreObj
                };

                console.log('✅ Hyperpure credentials loaded');
                this.initializeIframe();
                return;
            } 
            
            // If no query params, try other methods
            console.warn('⚠️ Missing Hyperpure credentials');
            this.tryLoadFromStorage();
        });
    }

    /**
     * Try loading data from session storage or localStorage
     * (Only if user is already authenticated)
     */
    private tryLoadFromStorage(): void {
        if (!this.isUserAuthenticated) {
            this.showError('User not authenticated');
            return;
        }

        // Method 2: From session storage
        try {
            const storedData = sessionStorage.getItem('hyperpureLoginData');
            if (storedData) {
                this.hyperpureLoginData = JSON.parse(storedData);
                console.log('✅ Login data loaded from session storage');
                this.initializeIframe();
                return;
            }
        } catch (e) {
            console.error('Failed to parse login data from session storage:', e);
        }

        // Method 3: From localStorage (fallback)
        try {
            const storedData = localStorage.getItem('hyperpureLoginData');
            if (storedData) {
                this.hyperpureLoginData = JSON.parse(storedData);
                console.log('✅ Login data loaded from localStorage');
                this.initializeIframe();
                return;
            }
        } catch (e) {
            console.error('Failed to parse login data from localStorage:', e);
        }

        // No login data found
        this.showError('No Hyperpure login data provided. Please authenticate first.');
    }

    /**
     * Initialize the iframe and load Hyperpure cart
     * (Only if user is authenticated)
     */
    private initializeIframe(): void {
        if (!this.isUserAuthenticated) {
            this.showError('User authentication required');
            return;
        }

        if (!this.hyperpureLoginData || !this.hyperpureLoginData.token) {
            this.showError('Invalid login data. Token is missing.');
            return;
        }

        // Sanitize and load iframe URL
        this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
        this.showIframe = true;
        document.body.style.overflow = 'hidden';

        // Wait for iframe to load, then send messages
        setTimeout(() => {
            this.isLoading = false;
        }, 2000);
    }

    /**
     * Setup message listener for iframe communication
     */
    private setupMessageListener(): void {
        window.addEventListener('message', (event: MessageEvent) => {
            // Security: Verify origin
            const isHyperpureOrigin = event.origin === this.trustedOrigin;
            const isOwnOrigin = event.origin === window.location.origin;

            if (!isHyperpureOrigin && !isOwnOrigin) {
                console.warn('❌ Rejected message from untrusted origin:', event.origin);
                return;
            }

            let response = event.data;

            // Parse JSON if string
            if (typeof response === 'string') {
                try {
                    response = JSON.parse(response);
                } catch (e) {
                    return;
                }
            }

            // Validate message structure
            if (!this.isValidMessage(response)) {
                return;
            }

            const topic = response.topic || response.Topic;

            console.log('📩 Received message:', topic);

            // Handle different message types
            if (topic === 'hyperpure_frame_loaded') {
                this.handleFrameLoaded();
            }

            if (topic === 'petpooja_store_registered') {
                if (this.listenerCalled) return;
                this.listenerCalled = true;
                this.handleStoreRegistration(response);
            }

            // Handle other Hyperpure events
            if (topic === 'hyperpure_cart_updated') {
                console.log('🛒 Cart updated in iframe');
                this.sharedService.successAlert('Cart updated successfully');
            }

            if (topic === 'hyperpure_checkout_complete') {
                console.log('✅ Checkout completed');
                this.sharedService.successAlert('Order placed successfully!');
            }
        }, false);
    }

    /**
     * Validate message structure
     */
    private isValidMessage(data: any): boolean {
        return (
            typeof data === 'object' &&
            (typeof data.Topic === 'string' || typeof data.topic === 'string')
        );
    }

    /**
     * Handle frame loaded event - Send login credentials
     */
    private handleFrameLoaded(): void {
        console.log('📨 Iframe loaded, sending login credentials...');
        this.sendLoginMessage();

        // After login, send cart load message
        setTimeout(() => {
            this.sendCartLoadMessage();
        }, 1000);
    }

    /**
     * Send login message to iframe (Topic 1: hyperpure_login)
     */
    private sendLoginMessage(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe element not found after 10 attempts');
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe || !iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe contentWindow not accessible');
                    return;
                }

                // Build login payload
                const payload = this.buildDispatcherData();

                // Send message to iframe
                iframe.contentWindow.postMessage(
                    JSON.stringify(payload),
                    this.iframeUrl
                );

                console.log('✅ Login message sent to iframe:', payload.Topic);

            } catch (err) {
                console.error('❌ Failed to send login message:', err);
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    /**
     * Build login dispatcher data (Topic 1)
     */
    private buildDispatcherData(): any {
        return {
            Topic: 'hyperpure_login',
            Data: this.hyperpureLoginData || {}
        };
    }

    /**
     * Send cart load message to iframe (Topic 2: hyperpure_frame_load_cart)
     */
    private sendCartLoadMessage(): void {
        const tryDispatch = (attempts = 0): void => {
            try {
                if (!this.iframeElement) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe element not found after 10 attempts');
                    return;
                }

                const iframe = this.iframeElement.nativeElement;
                if (!iframe || !iframe.contentWindow) {
                    if (attempts < 10) {
                        setTimeout(() => tryDispatch(attempts + 1), 500);
                        return;
                    }
                    console.error('❌ Iframe contentWindow not accessible');
                    return;
                }

                // Build cart load payload
                const payload = {
                    Topic: 'hyperpure_frame_load_cart',
                    Data: this.hyperpureLoginData || {}
                };

                // Send message to iframe
                iframe.contentWindow.postMessage(
                    JSON.stringify(payload),
                    this.iframeUrl
                );

                console.log('✅ Cart load message sent to iframe:', payload.Topic);

            } catch (err) {
                console.error('❌ Failed to send cart load message:', err);
                if (attempts < 5) {
                    setTimeout(() => tryDispatch(attempts + 1), 1000);
                }
            }
        };

        tryDispatch();
    }

    /**
     * Handle store registration event
     */
    private handleStoreRegistration(response: any): void {
        console.log('📝 Store registration received:', response);

        this.priceDiscoveryService.saveHyperpureStoreRegistration(response)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result: any) => {
                    this.sharedService.successAlert('Store registered successfully');
                    console.log('✅ Store registration saved:', result);
                },
                (error: any) => {
                    console.error('❌ Failed to save store registration:', error);
                    this.sharedService.errorAlert('Failed to save store registration');
                }
            );
    }

    /**
     * Reload the iframe
     */
    reloadIframe(): void {
        if (this.iframeUrl) {
            this.iframeUrlSafe = null;
            this.isLoading = true;

            setTimeout(() => {
                this.iframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.iframeUrl);
                setTimeout(() => this.isLoading = false, 2000);
            }, 10);

            this.sharedService.successAlert('Reloading Hyperpure cart...');
        }
    }

    /**
     * Close the iframe and go back
     */
    closeIframe(): void {
        this.showIframe = false;
        document.body.style.overflow = '';

        // Navigate back to purchase manager
        this.router.navigate(['/purchase-manager']);
    }

    /**
     * Show error message and redirect
     */
    private showError(message: string): void {
        this.sharedService.errorAlert(message);
        console.error('❌', message);

        // Redirect after 3 seconds
        setTimeout(() => {
            // Redirect to login if authentication failed, otherwise to purchase-manager
            if (!this.isUserAuthenticated) {
                this.router.navigate(['/login']);
            } else {
                this.router.navigate(['/purchase-manager']);
            }
        }, 3000);
    }

    /**
     * Manual trigger to send login message (for debugging)
     */
    manualSendLogin(): void {
        console.log('🔧 Manual login trigger');
        this.sendLoginMessage();
    }

    /**
     * Manual trigger to send cart load message (for debugging)
     */
    manualSendCartLoad(): void {
        console.log('🔧 Manual cart load trigger');
        this.sendCartLoadMessage();
    }
}
